import { Tag } from 'src/services/tag/Tag';

export function applyPreferredTagOrder<T extends Tag>(
  ids: string[],
  tags: T[]
): T[] {
  // Create a map using id if present, otherwise use fullPath
  const tagMap = new Map(tags.map((tag) => [tag.id || tag.fullPath, tag]));

  // Filter out unknown ids
  const knownIds = new Set(
    tags.map((tag) => tag.id).filter((id) => id !== undefined)
  );
  const filteredIds = ids.filter((id) => knownIds.has(id));

  const tree: { [key: string]: string[] } = {};
  tags.forEach((tag) => {
    const parts = tag.fullPath.split('/');
    let current = '';
    parts.forEach((part) => {
      const path = current ? `${current}/${part}` : part;
      if (!tree[current]) tree[current] = [];
      if (!tree[current].includes(path)) tree[current].push(path);
      current = path;
    });
  });

  function getDescendants(node: string): string[] {
    const descendants: string[] = [];
    const stack = [node];
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (tree[current]) {
        descendants.push(...tree[current]);
        stack.push(...tree[current]);
      }
    }
    return descendants;
  }

  function getIdOrFullPathFromFullPath(fullPath: string): string {
    const tag = tags.find((tag) => tag.fullPath === fullPath);
    return tag?.id || fullPath;
  }

  function customSort(a: string, b: string): number {
    const aIdOrPath = getIdOrFullPathFromFullPath(a);
    const bIdOrPath = getIdOrFullPathFromFullPath(b);
    const aIndex = filteredIds.indexOf(aIdOrPath);
    const bIndex = filteredIds.indexOf(bIdOrPath);

    // If both paths are not in the filteredIds array, sort by path depth first, then lexicographically
    if (aIndex === -1 && bIndex === -1) {
      const aDepth = a.split('/').length;
      const bDepth = b.split('/').length;
      if (aDepth !== bDepth) return aDepth - bDepth;
      return a.localeCompare(b);
    }

    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    const aDescendants = getDescendants(a);
    const bDescendants = getDescendants(b);

    if (aDescendants.includes(b)) return -1;
    if (bDescendants.includes(a)) return 1;

    return aIndex - bIndex;
  }

  const sortedRoot = (tree[''] || []).sort(customSort);

  function sortAndFlatten<T extends Tag>(nodes: string[]): T[] {
    const result: any[] = [];
    for (const fullPath of nodes) {
      const idOrPath = getIdOrFullPathFromFullPath(fullPath);
      if (tagMap.has(idOrPath)) {
        result.push(tagMap.get(idOrPath)!);
      }
      if (tree[fullPath]) {
        result.push(...sortAndFlatten(tree[fullPath].sort(customSort)));
      }
    }
    return result;
  }

  return sortAndFlatten(sortedRoot);
}
